import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { downloadAttachment } from "@/lib/files";
import { DocumentViewer } from "@/components/common/DocumentViewer";
import {
  ShieldCheck, FileText, Eye, Download, Briefcase, IdCard, CheckSquare, Pencil, Trash2, Copy, ListChecks, Users, LifeBuoy, UserRound, Info,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RelationshipsPanel } from "@/components/crm/RelationshipsPanel";
import { ProgramProgressCard } from "./overview/ProgramProgressCard";
import { RequirementsList } from "./overview/RequirementsList";
import { KeyDatesCard } from "./overview/KeyDatesCard";
import { RecentInteractions, meetingSplit } from "./overview/RecentInteractions";
import { Collapsible } from "./overview/Collapsible";
import { enrichRequirements, programLabel } from "./overview/programUtils";
import { LicenseRequirementSummary, LicensingHelp } from "@/components/licensing/LicensingHelp";

function PriorityInline({ opp, canManage }) {
  const [val, setVal] = useState(opp?.priority || "normal");
  const [opts, setOpts] = useState([]);
  useEffect(() => { api.get("/opportunity-field-options").then((r) => setOpts(r.data.priorities)).catch(() => setOpts([])); }, []);
  if (!opp) return null;
  const label = opts.find((o) => o.key === val)?.label || val;
  if (!canManage || !opts.length) return <span className="capitalize">{label}</span>;
  const save = async (v) => {
    const prev = val; setVal(v);
    try { await api.patch(`/opportunities/${opp.id}`, { priority: v }); toast.success("Priority updated"); }
    catch (e) { setVal(prev); toast.error(apiError(e)); }
  };
  return (
    <Select value={val} onValueChange={save}>
      <SelectTrigger className="h-7 w-28 text-sm" data-testid="overview-priority-select"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">{opts.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function DuplicateOppsCard({ opps, canManage }) {
  const active = (opps || []).filter((o) => !o.deleted && !o.close_date);
  const [removing, setRemoving] = useState(null);
  if (active.length < 2) return null;
  const remove = async (o) => {
    if (!window.confirm(`Remove duplicate opportunity ${o.human_id}? This is reversible and audited.`)) return;
    setRemoving(o.id);
    try { await api.delete(`/opportunities/${o.id}`); toast.success("Duplicate opportunity removed"); setTimeout(() => window.location.reload(), 600); }
    catch (e) { setRemoving(null); toast.error(apiError(e)); }
  };
  const sorted = [...active].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="overview-duplicate-opps">
      <h3 className="font-display font-semibold text-rose-800 flex items-center gap-2 text-sm mb-1"><Copy className="w-4 h-4" /> Duplicate Recruiting Opportunities</h3>
      <p className="text-xs text-rose-700 mb-3">This person has {active.length} active opportunities — only one is allowed. Keep the correct one (usually the oldest) and remove the others.</p>
      <div className="space-y-1.5">
        {sorted.map((o, i) => (
          <div key={o.id} className="flex items-center justify-between rounded-md bg-white border border-rose-100 px-3 py-2" data-testid={`dup-opp-${o.id}`}>
            <div className="text-sm text-slate-700">
              <span className="font-mono text-xs text-slate-500">{o.human_id}</span> · {o.pipeline_name || o.pipeline?.name || "Pipeline"} · {o.current_stage_name || "—"}
              {i === 0 && <span className="ml-2 text-[10px] rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5">oldest</span>}
            </div>
            {canManage && (
              <button onClick={() => remove(o)} disabled={removing === o.id} className="text-xs text-rose-600 hover:text-rose-800 flex items-center gap-1 disabled:opacity-50" data-testid={`dup-opp-remove-${o.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);
const isPdf = (n) => /\.pdf$/i.test(n || "");
const LIC_TONE = { complete: "bg-emerald-100 text-emerald-700", active: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", expired: "bg-rose-100 text-rose-700", needs_attention: "bg-rose-100 text-rose-700" };

function Facts({ rows }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!shown.length) return <p className="text-sm text-slate-400">No information on file.</p>;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
      {shown.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-slate-400">{k}</dt>
          <dd className="text-sm text-slate-800 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function ProfessionalNote({ person, canManage }) {
  const [val, setVal] = useState(person.professional_note || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/persons/${person.id}`, { professional_note: val }); toast.success("Professional note saved"); setEditing(false); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="professional-details-card">
      {editing ? (
        <div className="space-y-2">
          <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={4}
            className="w-full rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
            placeholder="Background, specialties, prior agency experience…" data-testid="prof-note-input" />
          <div className="flex gap-3 justify-end items-center">
            <button onClick={() => { setVal(person.professional_note || ""); setEditing(false); }} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
            <button onClick={save} disabled={saving} className="text-xs font-semibold bg-brand text-white rounded-md px-3 py-1.5 hover:bg-brand-strong disabled:opacity-50" data-testid="prof-note-save">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="prof-note-value">{val || <span className="text-slate-400">No professional note yet.</span>}</p>
          {canManage && (
            <button onClick={() => setEditing(true)} className="shrink-0 text-xs text-brand hover:underline flex items-center gap-1" data-testid="prof-note-edit"
              title="Professional details are managed here in Top-Recruit; imported values can be edited anytime.">
              <Pencil className="w-3.5 h-3.5" /> {val ? "Edit" : "Add note"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LicenseList({ licenses }) {
  if (!licenses.length) return null;
  return (
    <div className="space-y-2">
      <p className="text-[11px] text-slate-400">This person's own license records (staff/producer licenses are tracked on their own records).</p>
      {licenses.map((l) => {
        const raw = l.expiration_date || l.expires_at || l.expiration;
        const mmyy = /^(\d{1,2})\/(\d{4})$/.exec(raw || "");
        const exp = mmyy ? `${mmyy[2]}-${mmyy[1].padStart(2, "0")}-28` : raw;
        const expired = exp && !Number.isNaN(new Date(exp).getTime()) && new Date(exp) < new Date();
        const status = expired ? "expired" : (l.status || "active").toLowerCase();
        return (
          <div key={l.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`license-${l.id}`}>
            <div><p className="font-medium text-slate-800">{l.license_type || l.type || l.line || "License"} · {l.state || "—"}</p>
              <p className="text-xs text-slate-400">{(l.number || l.license_number) ? `#${l.number || l.license_number}` : ""}{l.issue_date ? ` · Obtained ${fmt(l.issue_date)}` : ""}{raw ? ` · Exp ${mmyy ? raw : fmt(raw)}` : ""}</p></div>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${LIC_TONE[status] || "bg-slate-100 text-slate-600"}`}>{expired ? "Expired" : (l.status || "Active")}</span>
          </div>
        );
      })}
    </div>
  );
}

function OpenTasksCard({ tasks, onCreateTask, onViewAll }) {
  const navigate = useNavigate();
  const open = (tasks || []).filter((t) => !["completed", "cancelled"].includes(t.status) && !t.archived && t.status !== "planned");
  const done = (tasks || []).filter((t) => t.status === "completed").length;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="overview-tasks">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-semibold text-sm text-slate-900 flex items-center gap-2"><CheckSquare className="w-4 h-4 text-brand" />Open Tasks</h3>
        {onCreateTask && <Button size="sm" variant="outline" onClick={onCreateTask} data-testid="overview-create-task">Create Task</Button>}
      </div>
      <p className="text-xs text-slate-500 mb-2" data-testid="overview-task-counts">{open.length} open · {done} completed</p>
      {open.length === 0 ? <p className="text-sm text-slate-400">No open tasks.</p> : (
        <div className="space-y-1">
          {open.slice(0, 4).map((t) => (
            <button key={t.id} onClick={() => navigate(`/tasks/detail/${t.id}`)} className="w-full flex items-center justify-between gap-2 text-left rounded-md hover:bg-slate-50 px-2 py-1.5" data-testid={`overview-task-${t.id}`}>
              <span className="text-sm text-slate-700 truncate">{t.title}</span>
              <span className={`text-xs shrink-0 ${t.due_date && new Date(t.due_date) < new Date() ? "text-rose-600" : "text-slate-400"}`}>{t.due_date ? fmt(t.due_date) : ""}</span>
            </button>
          ))}
          {open.length > 4 && <button onClick={onViewAll} className="text-xs font-medium text-brand hover:underline px-2" data-testid="overview-tasks-all">All {open.length} open tasks</button>}
        </div>
      )}
    </section>
  );
}

export function PersonOverview({ person, opps = [], tasks = [], timeline = [], notes = [], activities = [], attachments = [], agentProfile, primaryOpp,
  canManage, onCreateTask, onReload, keyInformation, supportContacts, stageTracker, onNavigate, ctLabel = (k) => k, activeCandidate = true }) {
  const { isManager, isAdmin, isGroupAdmin, isSuper } = useAuth();
  const [licenses, setLicenses] = useState(null);
  const [program, setProgram] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [viewerDoc, setViewerDoc] = useState(null);
  const cls = new Set(person.classifications || []);
  const isAgent = cls.has("agent");
  const isRecruit = cls.has("lead") || cls.has("candidate");
  const cf = person.custom_fields || {};
  const [licCtx, setLicCtx] = useState({});

  useEffect(() => {
    api.get(`/persons/${person.id}/licenses`).then((r) => { setLicenses(r.data.licenses || []); setLicCtx(r.data.help_context || {}); }).catch(() => setLicenses([]));
    api.get(`/programs/enrollments/${person.id}`).then((r) => setProgram(r.data)).catch(() => setProgram(null));
    api.get(`/persons/${person.id}/meetings`).then((r) => setMeetings(r.data.items || [])).catch(() => setMeetings([]));
  }, [person.id, person.updated_at]);

  const apptDate = cf.appointment_date || agentProfile?.appointment_date;
  const contractVer = cf.agency_contract_version || agentProfile?.agency_contract_version;
  const reqItems = enrichRequirements(program?.requirements, tasks);

  const attention = [];
  if (!person.organization_id) attention.push(["Missing organization", "Assign a district/organization"]);
  if (isRecruit && !person.assigned_recruiter_id && !opps.some((o) => !o.deleted && o.assigned_recruiter_id)) attention.push(["No assigned recruiter", "Assign a recruiter"]);
  if (isRecruit && opps.length === 0) attention.push(["No recruiting opportunity", "Start an opportunity"]);
  if (isAgent && !apptDate) attention.push(["Missing appointment date", "Add the appointment date"]);
  if (isAgent && !contractVer) attention.push(["Missing contract version", "Add the contract version"]);
  (licenses || []).forEach((l) => {
    const exp = l.expiration_date || l.expires_at || l.expiration;
    if (exp && new Date(exp) < new Date()) attention.push([`Expired license (${l.state || l.license_type || "license"})`, `Expired ${fmt(exp)}`]);
  });
  if (activeCandidate && program?.enrollment && program.waiting_on) attention.push([`Waiting on ${program.waiting_on.owner_label}`, program.waiting_on.requirement_title || program.waiting_on.message]);

  const { next, last } = meetingSplit(meetings);
  const contacts = [...(activities || []).map((a) => a.activity_at || a.created_at), last?.start].filter(Boolean).sort().reverse();
  const docs = (attachments || []).filter((a) => !a.archived).slice(0, 6);
  const canEditDates = !!(isSuper || isAdmin || isGroupAdmin || isManager);

  return (
    <div className="space-y-4">
      <DuplicateOppsCard opps={opps} canManage={canManage} />

      {activeCandidate && <ProgramProgressCard program={program} reqItems={reqItems} opp={primaryOpp} ctLabel={ctLabel} attention={attention} onViewMilestones={() => onNavigate?.("milestones")} />}

      {activeCandidate && program?.enrollment && reqItems.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="requirements-next-steps">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><ListChecks className="w-4 h-4 text-brand" />Requirements &amp; Next Steps</h2>
            <span className="text-xs text-slate-400">Overdue and blocked first</span>
          </div>
          <RequirementsList items={reqItems} limit={6} onViewAll={() => onNavigate?.("milestones")} />
        </section>
      )}

      {stageTracker}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {(primaryOpp || (activeCandidate && program?.enrollment)) && <KeyDatesCard person={person} opp={primaryOpp} enrollment={activeCandidate ? program?.enrollment : null} canEdit={canEditDates} onReload={onReload} />}
        {primaryOpp && (
          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="overview-recruiting">
            <h3 className="font-display font-semibold text-sm text-slate-900 flex items-center gap-2 mb-3"><Briefcase className="w-4 h-4 text-brand" />Recruiting Snapshot</h3>
            <Facts rows={[
              ["Lead Source", primaryOpp.source || person.source],
              ["Assigned Recruiter", primaryOpp.recruiter_name || person.assigned_recruiter_name || "Unassigned"],
              ["Priority", <PriorityInline key="p" opp={primaryOpp} canManage={canManage} />],
              ["Program", programLabel(program?.enrollment) || ctLabel(primaryOpp.candidate_type) || "Unknown"],
              ["Last Recruiter Contact", contacts[0] ? fmt(contacts[0]) : "No logged contact"],
              ["Next Appointment / Follow-up", next ? `${fmt(next.start)} · ${next.title || "Meeting"}` : primaryOpp.next_activity_at ? fmt(primaryOpp.next_activity_at) : "None scheduled"],
            ]} />
          </section>
        )}
        <RecentInteractions notes={notes} meetings={meetings} timeline={timeline} ctLabel={ctLabel} onViewAll={() => onNavigate?.("activities")} />
        <OpenTasksCard tasks={tasks} onCreateTask={onCreateTask} onViewAll={() => onNavigate?.("tasks")} />
      </div>

      <div className="space-y-2" data-testid="additional-information">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 pt-2">Additional Information</p>
        {keyInformation && <Collapsible title="Contact & Personal Information" icon={UserRound} testId="section-contact">{keyInformation}</Collapsible>}
        <Collapsible title="Professional Background" icon={Briefcase} testId="section-professional"><ProfessionalNote person={person} canManage={canManage} /></Collapsible>
        {(isAgent || agentProfile) && (
          <Collapsible title="Agent / Contract" icon={IdCard} testId="section-agent">
            <Facts rows={[
              ["Agent Code", agentProfile?.agent_number || cf.agent_number], ["Agency Contract Version", contractVer],
              ["Appointment Date", fmt(apptDate)], ["Agency / Business", agentProfile?.agency_name], ["Agency Model", agentProfile?.agent_type],
              ["Termination Date", fmt(agentProfile?.termination_date || cf.termination_date)],
            ]} />
          </Collapsible>
        )}
        <Collapsible title="Licensing & Compliance" icon={ShieldCheck} testId="section-licensing" empty={licenses !== null && !licenses.length}
          emptyText="No license records on file (not a confirmed missing qualification)">
          {licenses === null ? <p className="text-sm text-slate-400">Loading…</p> : <LicenseList licenses={licenses} />}
          {isRecruit && licenses !== null && <div className="mt-3" data-testid="staff-lic-help"><LicenseRequirementSummary licenses={licenses} /><LicensingHelp licenses={licenses} ctx={licCtx} /></div>}
          {person.proposed_office?.address && (
            <p className="text-xs text-slate-600 mt-2" data-testid="person-proposed-office"><span className="font-semibold">Proposed office (from candidate):</span>{" "}
              {[person.proposed_office.name, person.proposed_office.address, person.proposed_office.city, person.proposed_office.state, person.proposed_office.postal_code].filter(Boolean).join(", ")}</p>
          )}
        </Collapsible>
        <Collapsible title="Relationships" icon={Users} testId="section-relationships"><RelationshipsPanel bare recordType="person" recordId={person.id} /></Collapsible>
        <Collapsible title={`Documents${docs.length ? ` (${attachments.filter((a) => !a.archived).length})` : ""}`} icon={FileText} testId="section-documents" empty={!docs.length} emptyText="No documents on file">
          <div className="space-y-1">
            {docs.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 rounded-md hover:bg-slate-50 px-2 py-1" data-testid={`overview-doc-${a.id}`}>
                <button onClick={() => setViewerDoc(a)} className="flex items-center gap-2 min-w-0 text-left" data-testid={`overview-doc-open-${a.id}`}>
                  <FileText className={`w-4 h-4 shrink-0 ${isPdf(a.original_filename) ? "text-rose-500" : "text-slate-400"}`} />
                  <span className="text-sm text-slate-700 truncate hover:text-brand hover:underline">{a.original_filename || a.filename || "Document"}</span>
                </button>
                <span className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setViewerDoc(a)} className="p-1 rounded hover:bg-slate-100 text-slate-500" aria-label="View"><Eye className="w-4 h-4" /></button>
                  <button onClick={async () => { try { await downloadAttachment(`/attachments/${a.id}/file`, a.original_filename); } catch { toast.error("Couldn't download the document."); } }}
                    className="p-1 rounded hover:bg-slate-100 text-slate-400" aria-label="Download"><Download className="w-4 h-4" /></button>
                </span>
              </div>
            ))}
          </div>
        </Collapsible>
        {supportContacts && <Collapsible title="Support Contacts & Program Support" icon={LifeBuoy} testId="section-support">{supportContacts}</Collapsible>}
        <p className="text-[11px] text-slate-400 flex items-center gap-1 pt-1"><Info className="w-3 h-3" />Progress counts come from the same program requirements shown in Milestones.</p>
      </div>
      <DocumentViewer attachment={viewerDoc} open={!!viewerDoc} onClose={() => setViewerDoc(null)} />
    </div>
  );
}
